import React from 'react';
import { useTranslation } from 'react-i18next';
import { ROLES, recommendedFor } from '../rolePresets';

/**
 * Role step, media area: a radio list on the left and a live "Your rail"
 * preview on the right (included tools first, the rest dimmed). Picking a
 * role only changes attributes and the preview's text — the list itself is
 * never rebuilt, so nothing blinks.
 */
export default function RolePicker({ roleId, tools, onPick }) {
  const { t } = useTranslation();

  const onKeyDown = (e) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const idx = Math.max(0, ROLES.findIndex((r) => r.id === roleId));
    const next = ROLES[(idx + (e.key === 'ArrowDown' ? 1 : ROLES.length - 1)) % ROLES.length];
    onPick(next.id);
    const el = e.currentTarget.querySelector(`[data-role="${next.id}"]`);
    if (el) el.focus();
  };

  const keep = roleId ? new Set(recommendedFor(roleId)) : null;
  const kept = keep ? tools.filter((tool) => keep.has(tool.id)) : [];
  const ordered = keep ? [...kept, ...tools.filter((tool) => !keep.has(tool.id))] : tools;

  let summary = t('onboarding.role.pickHint');
  if (keep) {
    summary =
      kept.length === tools.length
        ? t('onboarding.role.countAll', { count: tools.length })
        : t('onboarding.role.countSome', { count: kept.length, total: tools.length });
    if (roleId === 'custom') summary += t('onboarding.role.customSuffix');
  }

  return (
    <div className="ob-rolepick">
      <div className="ob-rlist" role="radiogroup" aria-label={t('onboarding.role.groupLabel')} onKeyDown={onKeyDown}>
        {ROLES.map((role) => {
          const on = role.id === roleId;
          return (
            <button
              key={role.id}
              type="button"
              role="radio"
              aria-checked={on}
              tabIndex={on || (!roleId && role === ROLES[0]) ? 0 : -1}
              className="ob-rrow"
              data-role={role.id}
              onClick={() => onPick(role.id)}
            >
              <span className="ob-rrow-ic">
                <role.Icon size={16} />
              </span>
              <span className="ob-rrow-tx">
                <b>{t(`onboarding.roles.${role.id}.name`)}</b>
                <small>{t(`onboarding.roles.${role.id}.desc`)}</small>
              </span>
              <span className="ob-rrow-radio" aria-hidden="true" />
            </button>
          );
        })}
      </div>
      <div className="ob-rprev" aria-live="polite">
        <h6>{t('onboarding.role.railHeading')}</h6>
        <p className="ob-rprev-sum">{summary}</p>
        <div className="ob-rprev-grid">
          {ordered.map((tool) => (
            <span key={tool.id} className={`ob-rprev-it${keep && keep.has(tool.id) ? '' : ' ob-rprev-it--off'}`}>
              <tool.Icon size={14} aria-hidden="true" />
              {t(`onboarding.tool.${tool.key}.name`)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
